import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Button, Card, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { getCodiceSconto, createCodiceSconto, updateCodiceSconto } from '../services/api';

function CodiciScontoEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    codice: '',
    descrizione: '',
    tipo: 'PRIMO_ACQUISTO',
    tipo_sconto: 'PERCENTUALE',
    percentuale_sconto: 15,
    valido_dal: new Date().toISOString().split('T')[0],
    valido_fino: '',
    origine_marketplace: '',
    note: ''
  });

  useEffect(() => {
    if (!isNew && id) {
      fetchCodiceData();
    }
  }, [id]);

  const fetchCodiceData = async () => {
    setLoading(true);
    try {
      const response = await getCodiceSconto(id);
      const codice = response.data;
      setFormData({
        codice: codice.codice || '',
        descrizione: codice.descrizione || '',
        tipo: codice.tipo || 'PRIMO_ACQUISTO',
        tipo_sconto: codice.tipo_sconto || 'PERCENTUALE',
        percentuale_sconto: codice.percentuale_sconto || 0,
        valido_dal: codice.valido_dal ? new Date(codice.valido_dal).toISOString().split('T')[0] : '',
        valido_fino: codice.valido_fino ? new Date(codice.valido_fino).toISOString().split('T')[0] : '',
        origine_marketplace: codice.origine_marketplace || '',
        note: codice.note || ''
      });
      setLoading(false);
    } catch (err) {
      setError('Errore nel caricamento del codice sconto.');
      setLoading(false);
      console.error(err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validazione
    if (!formData.codice.trim()) {
      alert('Il campo Codice è obbligatorio');
      return;
    }
    const isSpeGratuita = formData.tipo === 'SPEDIZIONE_GRATUITA';
    if (!isSpeGratuita) {
      if (formData.percentuale_sconto < 0) {
        alert('Il valore sconto deve essere positivo');
        return;
      }
      if (formData.tipo_sconto === 'PERCENTUALE' && formData.percentuale_sconto > 100) {
        alert('La percentuale sconto deve essere tra 0 e 100');
        return;
      }
    }

    try {
      const payload = {
        ...formData,
        tipo_sconto: isSpeGratuita ? 'PERCENTUALE' : formData.tipo_sconto,
        percentuale_sconto: isSpeGratuita ? 0 : parseFloat(formData.percentuale_sconto),
        valido_dal: formData.valido_dal ? new Date(formData.valido_dal).toISOString() : null,
        valido_fino: formData.valido_fino ? new Date(formData.valido_fino).toISOString() : null,
        origine_marketplace: formData.origine_marketplace || null
      };

      if (isNew) {
        await createCodiceSconto(payload);
        alert('Codice sconto creato con successo!');
      } else {
        await updateCodiceSconto(id, payload);
        alert('Codice sconto aggiornato con successo!');
      }
      navigate('/codici-sconto');
    } catch (err) {
      alert('Errore durante il salvataggio: ' + (err.response?.data?.detail || err.message));
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  return (
    <div className="container mt-4">
      <Card>
        <Card.Header>
          <h4>{isNew ? 'Nuovo Codice Sconto' : 'Modifica Codice Sconto'}</h4>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Codice <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="codice"
                    value={formData.codice}
                    onChange={handleChange}
                    placeholder="es. PRIMO15-ABC123"
                    required
                    disabled={!isNew}
                  />
                  <Form.Text className="text-muted">
                    {isNew ? 'Il codice non può essere modificato dopo la creazione' : 'Campo non modificabile'}
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tipo</Form.Label>
                  <Form.Select
                    name="tipo"
                    value={formData.tipo}
                    onChange={handleChange}
                  >
                    <option value="PRIMO_ACQUISTO">Primo Acquisto</option>
                    <option value="GENERICO">Generico</option>
                    <option value="SPEDIZIONE_GRATUITA">Spedizione Gratuita</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {formData.tipo !== 'SPEDIZIONE_GRATUITA' && (
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tipo Sconto <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="tipo_sconto"
                    value={formData.tipo_sconto}
                    onChange={handleChange}
                  >
                    <option value="PERCENTUALE">Percentuale (%)</option>
                    <option value="IMPORTO_FISSO">Importo Fisso (€)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Valore Sconto {formData.tipo_sconto === 'PERCENTUALE' ? '(%)' : '(€)'}
                    {' '}<span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="percentuale_sconto"
                    value={formData.percentuale_sconto}
                    onChange={handleChange}
                    min="0"
                    max={formData.tipo_sconto === 'PERCENTUALE' ? 100 : undefined}
                    step="0.01"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Origine Marketplace</Form.Label>
                  <Form.Select
                    name="origine_marketplace"
                    value={formData.origine_marketplace}
                    onChange={handleChange}
                  >
                    <option value="">Nessuna</option>
                    <option value="FELTRINELLI">Feltrinelli</option>
                    <option value="IBS">IBS</option>
                    <option value="MARKETPLACE">Marketplace Generico</option>
                    <option value="ALTRO">Altro</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Marketplace di origine dell'ordine a cui era destinato il coupon
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Valido Dal</Form.Label>
                  <Form.Control
                    type="date"
                    name="valido_dal"
                    value={formData.valido_dal}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Valido Fino</Form.Label>
                  <Form.Control
                    type="date"
                    name="valido_fino"
                    value={formData.valido_fino}
                    onChange={handleChange}
                  />
                  <Form.Text className="text-muted">
                    Lasciare vuoto per nessuna scadenza
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Descrizione</Form.Label>
              <Form.Control
                type="text"
                name="descrizione"
                value={formData.descrizione}
                onChange={handleChange}
                placeholder="Descrizione interna (non visibile al cliente)"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Note</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="note"
                value={formData.note}
                onChange={handleChange}
                placeholder="Note aggiuntive"
              />
            </Form.Group>

            <div className="d-flex justify-content-between">
              <Button variant="secondary" onClick={() => navigate('/codici-sconto')}>
                Annulla
              </Button>
              <Button variant="primary" type="submit">
                {isNew ? 'Crea Codice' : 'Salva Modifiche'}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
}

export default CodiciScontoEdit;
